import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import api from "../lib/api";
import Logo from "../components/Logo";
import Markdown from "../components/Markdown";
import { ArrowRight, Eye, Sparkles, Star } from "lucide-react";

export default function SharePage() {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [brains, setBrains] = useState({});
  const [err, setErr] = useState("");
  const [featureBusy, setFeatureBusy] = useState(false);

  const isOwner = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const ownerId = doc?.user_id || doc?.owner_user_id;
      return !!user && !!ownerId && user.user_id === ownerId;
    } catch { return false; }
  })();

  useEffect(() => {
    // No credentials — public endpoint
    Promise.all([
      axios.get(`${API}/share/${id}`, { withCredentials: false, headers: {} }).then((r) => r.data),
      axios.get(`${API}/share/-/brains`, { withCredentials: false }).then((r) => r.data),
    ])
      .then(([d, b]) => {
        setDoc(d);
        const m = {}; (b.brains || []).forEach((x) => { m[x.id] = x; });
        setBrains(m);
      })
      .catch((e) => setErr(e.response?.data?.detail || "Share link not found"));
  }, [id]);

  const toggleFeature = async () => {
    if (!doc) return;
    setFeatureBusy(true);
    try {
      const next = !doc.featured;
      await api.post(`/shares/${id}/feature`, { featured: next });
      setDoc({ ...doc, featured: next });
    } catch (e) {
      alert(e.response?.data?.detail || "Feature toggle failed");
    } finally {
      setFeatureBusy(false);
    }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-[#FDF8F5] p-6">
      <div className="brut-card p-8 text-center max-w-md">
        <div className="text-5xl mb-3">🔗</div>
        <h1 className="font-heading text-2xl font-bold mb-1">This link isn't valid</h1>
        <p className="text-slate-600 mb-4">{err}</p>
        <Link to="/login" className="brut-btn"><Sparkles size={14} strokeWidth={2.5} /> Explore AiMedley</Link>
      </div>
    </div>
  );

  if (!doc) return <div className="min-h-screen flex items-center justify-center text-slate-400 italic">Loading share…</div>;

  const s = doc.snapshot || {};

  return (
    <div className="min-h-screen bg-[#FDF8F5]">
      {/* Top bar */}
      <header className="px-6 py-5 flex items-center justify-between max-w-5xl mx-auto">
        <Link to="/about"><Logo size={36} /></Link>
        <div className="flex items-center gap-3">
          <span className="flex items-center gap-1 text-xs text-slate-500 font-bold" data-testid="view-count">
            <Eye size={12} /> {doc.view_count} view{doc.view_count === 1 ? "" : "s"}
          </span>
          <Link to="/showcase" className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 font-bold" data-testid="share-showcase-link">
            <Sparkles size={12} strokeWidth={2.5} /> Showcase
          </Link>
          {isOwner && (
            <button
              onClick={toggleFeature}
              disabled={featureBusy}
              className={`inline-flex items-center gap-1 text-xs font-heading font-bold px-2 py-1 rounded-full border-2 ${doc.featured ? "bg-amber-200 border-slate-900 text-slate-900" : "bg-white border-slate-300 text-slate-600 hover:border-slate-900"}`}
              title={doc.featured ? "Remove from public showcase" : "Add to public showcase"}
              data-testid="feature-toggle"
            >
              <Star size={12} strokeWidth={2.5} className={doc.featured ? "fill-slate-900" : ""} />
              {featureBusy ? "…" : doc.featured ? "Featured" : "Feature"}
            </button>
          )}
          <Link to="/login" className="brut-btn text-sm" data-testid="share-cta-top">
            Try in AiMedley <ArrowRight size={12} strokeWidth={2.5} />
          </Link>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 pb-16">
        {doc.kind === "template" && <TemplateShare s={s} brain={brains[s.default_brain_id]} />}
        {doc.kind === "conversation" && <ConversationShare s={s} brains={brains} />}
        {doc.kind === "debate" && <DebateShare s={s} brains={brains} />}

        {/* CTA */}
        <div className="brut-card p-6 md:p-8 mt-8 text-center relative overflow-hidden" data-testid="share-cta-bottom">
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-amber-200 opacity-40 float-y" />
          <div className="relative">
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">Powered by AiMedley</div>
            <h3 className="font-heading text-2xl font-bold mb-2">Build your own AI workforce.</h3>
            <p className="text-slate-600 max-w-md mx-auto mb-4">
              Create AI workers with real personas, then power them with OpenAI, Claude, Gemini, DeepSeek, and more.
              Swap the brain anytime — the worker stays the same.
            </p>
            <Link to="/login" className="brut-btn"><Sparkles size={14} strokeWidth={2.5} /> Get started free</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateShare({ s, brain }) {
  return (
    <>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-4">
        Worker Template
      </div>
      <div className="brut-card p-6 md:p-8" data-testid="template-share">
        <div className="flex items-start gap-4 mb-5">
          <div className="w-20 h-20 rounded-2xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-4xl shrink-0">
            {s.avatar || "🤖"}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-heading text-3xl font-bold leading-tight">{s.name}</h1>
            <div className="text-slate-600 mt-0.5">{s.title}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mt-1">{s.department} · {s.role_type}</div>
            <div className="text-xs mt-2 text-slate-500">Shared by <b>{s.author_name}</b></div>
          </div>
        </div>
        {s.description && <p className="text-slate-700 mb-4">{s.description}</p>}
        <div className="grid sm:grid-cols-2 gap-4">
          {s.mission && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Mission</div>
              <p className="text-sm text-slate-800">{s.mission}</p>
            </div>
          )}
          {brain && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Recommended brain</div>
              <span className="inline-flex items-center gap-1.5 rounded-full border-2 font-heading font-bold px-2.5 py-1 text-xs"
                    style={{ backgroundColor: brain.color + "1F", borderColor: brain.color, color: brain.color }}>
                <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: brain.color }} />
                {brain.family} · {brain.name}
              </span>
            </div>
          )}
        </div>
        {(s.responsibilities || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Responsibilities</div>
            <ul className="list-disc ml-5 text-sm text-slate-700">
              {s.responsibilities.map((r, i) => <li key={`resp-${i}-${r.slice(0, 20)}`}>{r}</li>)}
            </ul>
          </div>
        )}
        {(s.persona?.expertise || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Expertise</div>
            <div className="flex flex-wrap gap-1.5">
              {s.persona.expertise.map((e, i) => (
                <span key={`exp-${e}-${i}`} className="px-2 py-0.5 rounded-full border-2 border-slate-900 bg-white text-xs font-semibold">{e}</span>
              ))}
            </div>
          </div>
        )}
        {(s.tools || []).length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Tools granted</div>
            <div className="flex flex-wrap gap-1.5">
              {s.tools.map((t, i) => <span key={`tool-${t}-${i}`} className="px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300 text-xs font-mono">{t}</span>)}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

function ConversationShare({ s, brains }) {
  const w = s.worker || {};
  return (
    <>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-4">
        Shared Conversation
      </div>
      <div className="brut-card p-5 mb-4 flex items-center gap-4" data-testid="conversation-header">
        <div className="w-16 h-16 rounded-2xl border-2 border-slate-900 bg-amber-100 flex items-center justify-center text-3xl">
          {w.avatar || "👤"}
        </div>
        <div>
          <h1 className="font-heading text-2xl font-bold leading-tight">{w.name}</h1>
          <div className="text-sm text-slate-600">{w.title}</div>
        </div>
      </div>

      <h2 className="font-heading text-xl font-bold mb-3" data-testid="conversation-title">💬 {s.conversation_title}</h2>

      <div className="space-y-3" data-testid="conversation-messages">
        {(s.messages || []).map((m, i) => {
          if (m.role === "user") return (
            <div key={m.message_id || `msg-${i}`} className="flex justify-end">
              <div className="max-w-[85%] bg-slate-900 text-white rounded-2xl rounded-br-sm px-4 py-3 font-medium">{m.content}</div>
            </div>
          );
          const brain = brains[m.brain_id];
          return (
            <div key={m.message_id || `msg-${i}`} className="flex justify-start">
              <div className="max-w-[90%] border-2 rounded-2xl rounded-bl-sm px-4 py-3 bg-white border-slate-300">
                <div className="flex items-center gap-2 mb-2 text-xs">
                  <span className="text-lg">{w.avatar}</span>
                  <span className="font-heading font-bold">{w.name}</span>
                  {brain && (
                    <span className="inline-flex items-center gap-1 rounded-full border-2 px-2 py-0.5 text-[10px] font-bold"
                          style={{ backgroundColor: brain.color + "1F", borderColor: brain.color, color: brain.color }}>
                      {brain.name}
                    </span>
                  )}
                </div>
                <Markdown text={m.content} />
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function DebateShare({ s, brains }) {
  const debatersById = {};
  (s.debaters || []).forEach((d) => { debatersById[d.worker_id] = d; });
  const rounds = Array.from({ length: s.rounds || 0 }, (_, i) => i + 1);
  return (
    <>
      <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-4">
        Debate
      </div>
      <div className="brut-card p-6 mb-5" data-testid="debate-header">
        <h1 className="font-heading text-2xl md:text-3xl font-bold mb-3">🎙️ {s.topic}</h1>
        <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Debaters</div>
        <div className="flex flex-wrap gap-2 mb-3">
          {(s.debaters || []).map((d) => (
            <div key={d.worker_id} className="flex items-center gap-2 bg-slate-50 border-2 border-slate-900 rounded-full px-3 py-1 text-sm">
              <span>{d.avatar}</span>
              <span className="font-heading font-bold">{d.name}</span>
              {d.stance && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">· {d.stance}</span>}
            </div>
          ))}
        </div>
        {s.judge && (
          <>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-1">Judge</div>
            <div className="flex items-center gap-2 bg-amber-50 border-2 border-slate-900 rounded-full px-3 py-1 text-sm inline-flex w-fit">
              <span>{s.judge.avatar}</span><span className="font-heading font-bold">{s.judge.name}</span>
            </div>
          </>
        )}
      </div>

      {rounds.map((r) => {
        const turns = (s.transcript || []).filter((t) => t.round === r);
        return (
          <div key={r} className="mb-5" data-testid={`debate-round-${r}`}>
            <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">Round {r}</div>
            <div className="space-y-3">
              {turns.map((t, i) => {
                const d = debatersById[t.worker_id] || {};
                const brain = brains[t.brain_id];
                return (
                  <div key={t.turn_id || `${t.worker_id}-${i}`} className="brut-card p-4">
                    <div className="flex items-center gap-2 mb-2 text-xs">
                      <span className="text-lg">{d.avatar}</span>
                      <span className="font-heading font-bold">{d.name}</span>
                      {brain && (
                        <span className="inline-flex items-center gap-1 rounded-full border-2 px-2 py-0.5 text-[10px] font-bold"
                              style={{ backgroundColor: brain.color + "1F", borderColor: brain.color, color: brain.color }}>
                          {brain.name}
                        </span>
                      )}
                    </div>
                    <Markdown text={t.content} />
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {s.verdict && (
        <div className="brut-card p-6 bg-amber-50" data-testid="debate-verdict">
          <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-2">
            ⚖️ AI Judge Verdict by {s.judge?.name}
          </div>
          <Markdown text={s.verdict.content || ""} />
        </div>
      )}

      <DebatePoll shareId={window.location.pathname.split("/").pop()} debaters={s.debaters || []} />

      <div className="mt-6 flex justify-center gap-2 flex-wrap">
        <button
          onClick={() => {
            const sid = window.location.pathname.split("/").pop();
            window.open(`${API}/share/${sid}/export.md`, "_blank");
          }}
          className="brut-btn brut-btn-outline text-sm"
          data-testid="share-export-btn"
        >
          ⬇ Export as Markdown
        </button>
        <button
          onClick={() => {
            const sid = window.location.pathname.split("/").pop();
            window.open(`${API}/share/${sid}/export.pdf`, "_blank");
          }}
          className="brut-btn brut-btn-outline text-sm"
          data-testid="share-export-pdf-btn"
        >
          ⬇ Export as PDF
        </button>
      </div>
    </>
  );
}

/* -------------------- Debate Poll (audience vote) -------------------- */
function DebatePoll({ shareId, debaters }) {
  const [tally, setTally] = useState({});
  const [total, setTotal] = useState(0);
  const [yourVote, setYourVote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Anonymous per-device token
  const token = React.useMemo(() => {
    let t = localStorage.getItem("aimedley_voter_token");
    if (!t) {
      t = "v_" + Math.random().toString(36).slice(2, 12) + Date.now().toString(36);
      localStorage.setItem("aimedley_voter_token", t);
    }
    return t;
  }, []);

  useEffect(() => {
    axios.get(`${API}/share/${shareId}/vote`, { params: { voter_token: token } })
      .then((r) => {
        setTally(r.data.tally || {});
        setTotal(r.data.total || 0);
        setYourVote(r.data.your_vote || null);
      })
      .finally(() => setLoading(false));
  }, [shareId, token]);

  const vote = async (worker_id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/share/${shareId}/vote`, { worker_id, voter_token: token });
      setTally(r.data.tally || {});
      setTotal(r.data.total || 0);
      setYourVote(r.data.your_vote || worker_id);
    } catch (e) {
      alert(e.response?.data?.detail || "Vote failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !debaters?.length) return null;
  const revealed = yourVote !== null;

  return (
    <div className="brut-card p-5 mt-6" data-testid="debate-poll">
      <div className="text-xs uppercase tracking-widest font-bold text-slate-500 mb-1">🗳️ Audience vote</div>
      <div className="font-heading font-bold text-lg mb-3">
        {yourVote ? "You voted — see how the crowd feels" : "Who do you think won?"}
      </div>
      <div className="space-y-2">
        {debaters.map((d) => {
          const count = tally[d.worker_id] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const mine = yourVote === d.worker_id;
          return (
            <button
              key={d.worker_id}
              disabled={busy}
              onClick={() => vote(d.worker_id)}
              className={`w-full brut-card px-3 py-2.5 text-left relative overflow-hidden hover:-translate-y-0.5 transition-transform ${mine ? "ring-2 ring-emerald-500" : ""}`}
              data-testid={`vote-${d.worker_id}`}
            >
              {revealed && (
                <div className="absolute inset-y-0 left-0 bg-amber-200/60" style={{ width: `${pct}%` }} />
              )}
              <div className="relative flex items-center gap-2 text-sm">
                <span className="text-lg">{d.avatar}</span>
                <span className="font-heading font-bold">{d.name}</span>
                {d.stance && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">· {d.stance}</span>}
                {mine && <span className="text-[9px] uppercase tracking-widest font-bold text-emerald-700 border border-emerald-600 rounded-full px-1.5 py-0.5 bg-emerald-50">Your vote</span>}
                <span className="ml-auto text-xs font-mono text-slate-600">
                  {revealed ? `${pct}% · ${count}` : "Vote"}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      {revealed && (
        <div className="mt-3 text-[10px] text-slate-500">
          {total} vote{total === 1 ? "" : "s"} · One vote per device · You can change your pick.
        </div>
      )}
    </div>
  );
}
